/**
 * Vollanalyse S6: gemeinsamer Web-Vertrag für die feldgenauen 400er der API
 * (`BadRequestException({ message, errors: [{ field, message }] })` — u. a.
 * finalize.service, collective.service, settings.service, datev.service,
 * orders.service, master-data/maintenance). Der globale A2-6-Toast unterdrückt
 * genau diese Form (lib/queryClient.isFieldValidationError) — jede Seite, die
 * so einen Fehler auslösen kann, MUSS die Details also selbst rendern, sonst
 * verschwinden sie. Vorher parsten die Konsumenten den Body in je eigenen
 * Idiomen (oder zeigten nur `message` ohne die Details).
 */
import { ApiError } from '@werkbank/shared';

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Strukturierte Feld-Fehler eines Wurfs lesen; null, wenn der Fehler nicht
 * dem Vertrag entspricht (kein ApiError, kein 400, kein errors[]-Array).
 */
export function fieldErrorsOf(error: unknown): FieldError[] | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null || !('errors' in body)) return null;
  const errors = (body as { errors: unknown }).errors;
  if (!Array.isArray(errors)) return null;
  return errors
    .filter(
      (e): e is FieldError =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as { message?: unknown }).message === 'string',
    )
    .map((e) => {
      // Faruq 2026-09-07: Zod-Issues der ZodValidationPipe (nestjs-zod) tragen
      // statt `field` einen `path` — ohne ihn stuende in der Anzeige nur
      // „Datum als JJJJ-MM-TT erwartet." ohne Ort.
      const roh = e as FieldError & { path?: unknown };
      const field =
        typeof roh.field === 'string'
          ? roh.field
          : Array.isArray(roh.path)
            ? roh.path.map(String).join('.')
            : '';
      return { field, message: e.message };
    });
}

/**
 * Kopf-Meldung plus Detail-Meldungen als einen anzeigbaren String — für
 * Konsumenten mit einer einzelnen Fehlerfläche (Alert/Text). Fällt ohne
 * Feld-Fehler auf die normale Fehlermeldung zurück.
 */
export function formatFieldErrors(error: unknown, fallback = 'Aktion nicht möglich.'): string {
  const details = fieldErrorsOf(error) ?? [];
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error && error.message
        ? error.message
        : fallback;
  return [message, ...details.map((d) => d.message)].join(' ').trim();
}
